// Circular SVG countdown timer

export interface CountdownTimerController {
  root: HTMLElement;
  start: () => void;
  stop: () => void;
  reset: () => void;
}

export function createCountdownTimer(
  durationSeconds: number,
  onComplete: () => void
): CountdownTimerController {
  const circumference = 2 * Math.PI * 45; // radius 45
  let startTime = 0;
  let rafId = 0;
  let remaining = durationSeconds * 1000;
  let running = false;

  const root = document.createElement('div');
  root.className = 'relative w-28 h-28 mx-auto';

  root.innerHTML = `
    <svg class="w-28 h-28 transform -rotate-90">
      <circle cx="56" cy="56" r="45" stroke="#E5E7EB" stroke-width="10" fill="none" />
      <circle id="progress-ring" cx="56" cy="56" r="45" stroke="#2563EB" stroke-width="10" fill="none"
        stroke-dasharray="${circumference}"
        stroke-dashoffset="0"
        stroke-linecap="round"
      />
    </svg>
    <div class="absolute inset-0 flex items-center justify-center">
      <div class="text-center">
        <div class="text-xs text-gray-500 uppercase tracking-wide">Seconds</div>
        <div id="timer-label" class="text-3xl font-semibold text-gray-900">${durationSeconds}</div>
      </div>
    </div>
  `;

  const progressRing = root.querySelector<SVGCircleElement>('#progress-ring')!;
  const timerLabel = root.querySelector<HTMLDivElement>('#timer-label')!;

  const tick = (timestamp: number) => {
    if (!running) return;
    if (!startTime) startTime = timestamp;

    const elapsed = timestamp - startTime;
    const msLeft = Math.max(0, remaining - elapsed);
    const secondsLeft = Math.ceil(msLeft / 1000);

    const progress = elapsed / remaining;
    progressRing.style.strokeDashoffset = String(progress * circumference);
    timerLabel.textContent = String(secondsLeft);

    if (msLeft <= 0) {
      running = false;
      cancelAnimationFrame(rafId);
      timerLabel.textContent = '0';
      progressRing.style.strokeDashoffset = String(circumference);
      onComplete();
      return;
    }

    rafId = requestAnimationFrame(tick);
  };

  const start = () => {
    if (running) return;
    running = true;
    startTime = 0;
    rafId = requestAnimationFrame(tick);
  };

  const stop = () => {
    running = false;
    cancelAnimationFrame(rafId);
  };

  const reset = () => {
    stop();
    remaining = durationSeconds * 1000;
    progressRing.style.strokeDashoffset = '0';
    timerLabel.textContent = String(durationSeconds);
  };

  return { root, start, stop, reset };
}
